import { Box } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import {
  getInputBackgroundColor,
  getInputBorderColor,
  getInputErrorBackgroundColor,
  getInputErrorShadow,
  getInputTextColor,
  inputErrorBorderColor,
  inputFocusBorderColor,
  inputRadius,
} from "#app/theme/v2/inputStates";
import { spacing } from "#app/theme/v2/spacing";

import { TEXT_LETTER_SPACING, TEXT_LINE_HEIGHTS } from "../Typographies/Text";
import classes from "./Input.module.css";

type InputSize = "sm" | "md";

const INPUT_SIZES: Record<
  InputSize,
  { height: string; paddingX: string; fontSize: string; lineHeight: string; letterSpacing: string }
> = {
  sm: {
    height: spacing[8],
    paddingX: spacing[3],
    fontSize: fontSizes[12],
    lineHeight: TEXT_LINE_HEIGHTS.tiny,
    letterSpacing: TEXT_LETTER_SPACING.regular.tiny,
  },
  md: {
    height: spacing[9],
    paddingX: spacing[4],
    fontSize: fontSizes[14],
    lineHeight: TEXT_LINE_HEIGHTS.small,
    letterSpacing: TEXT_LETTER_SPACING.regular.small,
  },
};

type InputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  type?: "text" | "number";
  size?: InputSize;
  disabled?: boolean;
  error?: boolean;
  min?: number;
  "aria-label"?: string;
};

const Input = ({
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  size = "md",
  disabled,
  error,
  min,
  "aria-label": ariaLabel,
}: InputProps) => {
  const { height, paddingX, fontSize, lineHeight, letterSpacing } = INPUT_SIZES[size];
  const hasValue = Boolean(value);

  return (
    <Box
      className={classes.container}
      data-error={error || undefined}
      style={
        {
          height,
          paddingInline: paddingX,
          borderRadius: inputRadius,
          "--container-bg": getInputBackgroundColor({ disabled, hasValue }),
          "--container-bd": getInputBorderColor({ disabled, hasValue }),
          "--container-focus-bd": inputFocusBorderColor,
          "--container-error-bd": inputErrorBorderColor,
          "--container-error-bg": getInputErrorBackgroundColor({ hasValue }),
          "--container-error-shadow": getInputErrorShadow({ hasValue }),
        } as React.CSSProperties
      }
    >
      <input
        className={classes.field}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        min={min}
        aria-label={ariaLabel}
        style={
          {
            "--field-fz": fontSize,
            "--field-fw": fontWeights.regular,
            "--field-lh": lineHeight,
            "--field-ls": letterSpacing,
            "--field-color": colors.text.primaryDefault,
            "--field-placeholder-color": colors.text.secondaryDefault,
            "--field-disabled-color": getInputTextColor({ disabled, hasValue }),
            "--field-disabled-placeholder-color": colors.text.secondaryDisabled,
          } as React.CSSProperties
        }
      />
    </Box>
  );
};

export { Input };
